import { useState } from 'react';
import { api } from '../../api.js';
import { useNearTaxonomy } from './taxonomy.js';
import { SHIP_REGIONS } from '../../../../shared/online-stores.js';
import {
  orderedCaps,
  distanceLabel,
  openState,
  weekRows,
  meetsLine,
  eventDate,
  eventTime,
  eventOnNow,
  priceLabel,
  zoneTag,
} from './helpers.js';
import {
  card,
  cardBody,
  bannerBox,
  bannerPlaceholder,
  bannerInitial,
  bannerLogo,
  logoMark,
  logoFallback,
  logoFallbackInner,
  matchLine,
  matchChip,
  moreBtn,
  hoursRow,
  dot,
  weekPanel,
  weekLine,
  tag,
  btnPrimary,
  btnGhost,
  suggestLink,
} from './styles.js';

export function CardBanner({ name, imageUrl, logoUrl, kind }) {
  if (imageUrl) {
    return (
      <div
        style={{
          ...bannerBox,
          backgroundImage: `url("${imageUrl}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    );
  }
  // No photo but the place has a logo — show the logo on the accent strip
  // instead of a generic initial; it's what a keeper actually recognises.
  if (logoUrl) {
    return (
      <div style={bannerPlaceholder}>
        <img src={logoUrl} alt={name || ''} style={bannerLogo} />
      </div>
    );
  }
  const initial = (name || '?').trim()[0]?.toUpperCase() || '?';
  const glyph = kind === 'event' ? '📅' : kind === 'club' ? '🐠' : initial;
  return (
    <div style={bannerPlaceholder}>
      <span style={bannerInitial}>{glyph}</span>
    </div>
  );
}

// Logo, not photograph. A store's interior shot is 400px of blue light
// that tells a keeper nothing and pushes distance, hours and the
// Directions button below the fold — and because only some places have
// uploaded one, photo-havers visually outrank everyone else for a reason
// unrelated to the store. The logo is what a keeper recognises from the
// storefront and Instagram, so it leads instead. The photo is still
// stored and still shown where a picture IS the content: frag listings
// and event flyers.
export function PlaceMark({ name, logoUrl, glyph }) {
  if (logoUrl) return <img src={logoUrl} alt="" style={logoMark} />;
  const initial = (name || '?').trim()[0]?.toUpperCase() || '?';
  return (
    <div style={logoFallback}>
      <div style={logoFallbackInner}>{glyph || initial}</div>
    </div>
  );
}

export function StoreCard({ p, onSuggestEdit, unit = 'mi' }) {
  const { labels } = useNearTaxonomy();
  const caps = orderedCaps(p, labels);
  const [showHours, setShowHours] = useState(false);
  const [showAllCaps, setShowAllCaps] = useState(false);
  const os = openState(p.hours, { tz: p.tz, zoneLabel: true });
  const rows = weekRows(p.hours);
  const storeZone = zoneTag(p.tz).trim();
  const dist = distanceLabel(p.distance_mi, unit);
  const reasons = Array.isArray(p.reasons) ? p.reasons.slice(0, 3) : [];
  const CAP_PREVIEW = 4;
  const shownCaps = showAllCaps ? caps : caps.slice(0, CAP_PREVIEW);
  const hiddenCaps = caps.length - shownCaps.length;
  return (
    <div style={card}>
      <div style={cardBody}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <PlaceMark name={p.name} logoUrl={p.logo_url} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 650, letterSpacing: '-0.01em' }}>{p.name}</div>
            {p.rating ? (
              <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 2 }}>
                &#9733; {Number(p.rating).toFixed(1)}
                {p.city ? ` · ${p.city}` : ''}
              </div>
            ) : p.city ? (
              <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 2 }}>{p.city}</div>
            ) : null}
          </div>
          {dist ? (
            <div
              style={{
                fontSize: 15,
                color: 'var(--text-muted)',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {dist}
            </div>
          ) : null}
        </div>

        {/* Dynamic open/closed status — reads the clock, not a text dump */}
        {os ? (
          os.text ? (
            <div style={{ fontSize: 14.5, color: 'var(--text-muted)', marginTop: 10 }}>
              <span style={{ color: 'var(--text-dim)' }}>Hours </span>
              {os.text}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => rows && setShowHours((v) => !v)}
              style={hoursRow(!!rows)}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <span style={dot(os.open)} />
                <b
                  style={{
                    color: os.open ? 'var(--good, #37b26b)' : 'var(--text)',
                    fontWeight: 650,
                  }}
                >
                  {os.open ? 'Open now' : 'Closed'}
                </b>
                <span style={{ color: 'var(--text-muted)' }}>
                  · {os.label.replace(/^Open · /, '').replace(/^Opens/, 'opens')}
                </span>
              </span>
              {rows ? (
                <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>
                  {showHours ? 'Hide' : 'All hours'}
                </span>
              ) : null}
            </button>
          )
        ) : null}
        {showHours && rows ? (
          <div style={weekPanel}>
            {storeZone ? (
              <div style={{ ...weekLine, color: 'var(--text-dim)' }}>Hours in {storeZone}</div>
            ) : null}
            {rows.map((r) => (
              <div key={r.label} style={weekLine}>
                <span style={{ color: 'var(--text-muted)' }}>{r.label}</span>
                <span style={{ color: r.txt === 'Closed' ? 'var(--text-dim)' : 'var(--text)' }}>
                  {r.txt}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {/* A store with no hours rendered nothing at all, so it silently
            read as "no information" next to a neighbour showing Open now.
            Naming the gap is honest and invites the crowd edit that fills
            it — driving somewhere closed is the failure this prevents. */}
        {!os ? (
          <div style={{ fontSize: 14.5, color: 'var(--text-dim)', marginTop: 10 }}>
            Hours not listed
          </div>
        ) : null}

        {/* Why this place ranks where it does. rankPlaces already computes
            these against the keeper's own tank; without them on the card
            the ordering looked arbitrary. */}
        {reasons.length ? (
          <div style={matchLine}>
            {reasons.map((r) => (
              <span key={r} style={matchChip}>
                {r}
              </span>
            ))}
          </div>
        ) : null}

        {caps.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {shownCaps.map((k) => (
              <span key={k} style={tag}>
                {labels[k]}
              </span>
            ))}
            {hiddenCaps > 0 ? (
              <button type="button" onClick={() => setShowAllCaps(true)} style={moreBtn}>
                +{hiddenCaps} more
              </button>
            ) : null}
            {showAllCaps && caps.length > CAP_PREVIEW ? (
              <button type="button" onClick={() => setShowAllCaps(false)} style={moreBtn}>
                Show less
              </button>
            ) : null}
          </div>
        ) : null}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            marginTop: 14,
          }}
        >
          {p.phone ? (
            <a href={`tel:${p.phone}`} className="font-heading" style={btnGhost}>
              Call
            </a>
          ) : null}
          {p.website ? (
            <a
              href={/^https?:\/\//.test(p.website) ? p.website : `https://${p.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-heading"
              style={btnGhost}
            >
              Website
            </a>
          ) : null}
          {p.lat != null && p.lng != null ? (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-heading"
              style={btnPrimary}
            >
              Directions
            </a>
          ) : null}
        </div>
        {onSuggestEdit ? (
          <button type="button" onClick={() => onSuggestEdit(p)} style={suggestLink}>
            Suggest an edit
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function ClubCard({ p, onSuggestEdit, unit = 'mi' }) {
  const dist = distanceLabel(p.distance_mi, unit);
  return (
    <div style={card}>
      <div style={cardBody}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <PlaceMark name={p.name} logoUrl={p.logo_url} glyph="🐠" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 650, letterSpacing: '-0.01em' }}>{p.name}</div>
            <div
              style={{ fontSize: 13, color: 'var(--cyan)', marginTop: 3, letterSpacing: '0.04em' }}
            >
              REEF CLUB{p.city ? ` · ${p.city}` : ''}
            </div>
          </div>
          {dist ? (
            <div
              style={{
                fontSize: 15,
                color: 'var(--text-muted)',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              {dist}
            </div>
          ) : null}
        </div>
        {p.address ? (
          <div style={{ fontSize: 14.5, color: 'var(--text-muted)', marginTop: 8 }}>
            {p.address}
          </div>
        ) : null}
        {meetsLine(p.hours) ? (
          <div style={{ fontSize: 14.5, color: 'var(--text-muted)', marginTop: 6 }}>
            <span style={{ color: 'var(--text-dim)' }}>Meets </span>
            {meetsLine(p.hours)}
            {p.hours?.days && zoneTag(p.tz) ? ` (${zoneTag(p.tz).trim()})` : ''}
          </div>
        ) : null}
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            marginTop: 14,
          }}
        >
          {p.website ? (
            <a
              href={/^https?:\/\//.test(p.website) ? p.website : `https://${p.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-heading"
              style={btnPrimary}
            >
              Join / visit
            </a>
          ) : null}
          {p.lat != null && p.lng != null ? (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-heading"
              style={btnGhost}
            >
              Directions
            </a>
          ) : null}
        </div>
        {onSuggestEdit ? (
          <button type="button" onClick={() => onSuggestEdit(p)} style={suggestLink}>
            Suggest an edit
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function EventCard({ e }) {
  const [going, setGoing] = useState(!!e.you_going);
  const [count, setCount] = useState(e.going || 0);
  const [busy, setBusy] = useState(false);
  const [rsvpErr, setRsvpErr] = useState('');
  const toggleRsvp = async () => {
    const next = !going;
    setBusy(true);
    setRsvpErr('');
    // Optimistic — reflect the tap immediately, roll back on failure.
    setGoing(next);
    setCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      await api(`/api/near/events/${e.id}/rsvp`, { method: 'POST', body: { going: next } });
    } catch {
      setGoing(!next);
      setCount((c) => Math.max(0, c + (next ? -1 : 1)));
      // The button flipped back with no word, so a keeper who looked away
      // believed a reminder was coming.
      setRsvpErr(
        next
          ? 'Could not save that. You are not marked as going.'
          : 'Could not save that. You are still marked as going.'
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={card}>
      <CardBanner name={e.title} imageUrl={e.image_url} kind="event" />
      <div style={cardBody}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 650, letterSpacing: '-0.01em' }}>{e.title}</div>
            <div style={{ fontSize: 14.5, color: 'var(--cyan)', marginTop: 3 }}>
              {eventOnNow(e) ? <strong>On now · </strong> : null}
              {eventDate(e.starts_at, e.tz)}
              {eventTime(e.starts_at, e.tz)
                ? ` · ${eventTime(e.starts_at, e.tz)}${zoneTag(e.tz, e.starts_at)}`
                : ''}
            </div>
            {e.venue_text ? (
              <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 2 }}>
                {e.venue_text}
              </div>
            ) : null}
          </div>
        </div>
        {e.details ? (
          <div
            style={{ fontSize: 14.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}
          >
            {e.details}
          </div>
        ) : null}
        {Array.isArray(e.tags) && e.tags.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {e.tags.map((t) => (
              <span key={t} style={tag}>
                {t}
              </span>
            ))}
          </div>
        ) : null}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginTop: 12,
          }}
        >
          <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            {count ? (
              <>
                <b style={{ color: 'var(--text)' }}>{count}</b> going
              </>
            ) : (
              'Be the first to go'
            )}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            {e.lat != null && e.lng != null ? (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${e.lat},${e.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-heading"
                style={btnGhost}
              >
                Directions
              </a>
            ) : null}
            <button
              type="button"
              onClick={toggleRsvp}
              disabled={busy}
              className="font-heading"
              style={{ ...(going ? btnGhost : btnPrimary), opacity: busy ? 0.6 : 1 }}
            >
              {going ? '✓ Going' : "I'm going"}
            </button>
          </div>
        </div>
        {rsvpErr ? (
          <div role="alert" style={{ fontSize: 14, color: 'var(--alert)', marginTop: 8 }}>
            {rsvpErr}
          </div>
        ) : going ? (
          <div style={{ fontSize: 14, color: 'var(--text-dim)', marginTop: 8 }}>
            {pushOn()
              ? 'We will remind you by push before it starts.'
              : 'Turn on notifications in Account to get a reminder before it starts.'}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// The reminder for an event comes only by push (never email or text).
function pushOn() {
  try {
    return typeof Notification !== 'undefined' && Notification.permission === 'granted';
  } catch {
    return false;
  }
}

export function FragCard({ p, isOwner, onSold, unit = 'mi' }) {
  const [sold, setSold] = useState(false);
  const markSold = async () => {
    if (!confirm('Mark this listing sold? It will be removed from the feed.')) return;
    setSold(true);
    try {
      await api(`/api/near/frags/${p.id}/sold`, { method: 'POST' });
      onSold?.(p.id);
    } catch {
      setSold(false);
    }
  };
  return (
    <div style={card}>
      <CardBanner name={p.title} imageUrl={p.image_url} />
      <div style={cardBody}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 650, letterSpacing: '-0.01em' }}>{p.title}</div>
            {p.species ? (
              <div style={{ fontSize: 14.5, color: 'var(--text-muted)', marginTop: 2 }}>
                {p.species}
              </div>
            ) : null}
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <div style={{ fontSize: 16, fontWeight: 650, color: 'var(--cyan)' }}>
              {priceLabel(p)}
            </div>
            {distanceLabel(p.distance_mi, unit) ? (
              <div
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {distanceLabel(p.distance_mi, unit)}
                {p.city ? ` · ${p.city}` : ''}
              </div>
            ) : p.city ? (
              <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{p.city}</div>
            ) : null}
          </div>
        </div>
        {p.details ? (
          <div
            style={{ fontSize: 14.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}
          >
            {p.details}
          </div>
        ) : null}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginTop: 12,
          }}
        >
          <span
            style={{
              fontSize: 14,
              color: 'var(--text-muted)',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {p.contact ? (
              <>
                <span style={{ color: 'var(--text-dim)' }}>Contact </span>
                {p.contact}
              </>
            ) : (
              'No contact provided'
            )}
          </span>
          {isOwner ? (
            <button
              type="button"
              onClick={markSold}
              disabled={sold}
              className="font-heading"
              style={{ ...btnGhost, opacity: sold ? 0.6 : 1 }}
            >
              {sold ? 'Sold' : 'Mark sold'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const REGION_LABEL = Object.fromEntries(SHIP_REGIONS.map((r) => [r.k, r.label]));

// An online store: no map pin, no hours; where it ships and what it sells.
export function OnlineCard({ p }) {
  const { labels } = useNearTaxonomy();
  const caps = orderedCaps(p, labels).slice(0, 6);
  const ships = (p.ships_to || []).map((k) => REGION_LABEL[k] || k);
  return (
    <div style={card}>
      <div style={cardBody}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <PlaceMark name={p.name} logoUrl={p.logo_url} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 650, letterSpacing: '-0.01em' }}>{p.name}</div>
            <div style={{ fontSize: 14.5, color: 'var(--text-muted)', marginTop: 2 }}>
              {p.ships_here === true ? (
                <b style={{ color: 'var(--good, #37b26b)', fontWeight: 650 }}>Ships to you · </b>
              ) : p.ships_here === false ? (
                'May not ship to you · '
              ) : null}
              {ships.length ? `Ships to ${ships.join(', ')}` : 'Shipping not listed'}
            </div>
          </div>
        </div>
        {caps.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {caps.map((k) => (
              <span key={k} style={tag}>
                {labels[k] || k}
              </span>
            ))}
          </div>
        ) : null}
        {p.website ? (
          <a
            href={p.website}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', marginTop: 12, minHeight: 44, lineHeight: '44px' }}
          >
            Visit their shop
          </a>
        ) : null}
      </div>
    </div>
  );
}
